import moment from "moment";
import pluralize from "pluralize";
import { useContext } from "react";
import { BootstrapContext } from "../contexts/BootstrapContext";

export function getInternationalFormattedDate() {
  return moment().format("YYYY-MMM-DD");
}

export function getPrettyDate(timestamp, atLocalTime = true) {
  // e.g 1753891030 -> "Jul 30, 2025"
  if (!timestamp) return "";

  const date = new Date(timestamp * 1000);
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: atLocalTime ? undefined : "UTC",
  };
  return date.toLocaleDateString("en-US", options);
}

export function getReadableTime(timestamp, atLocalTime = true) {
  // e.g 1753891562 -> "19:06:02" or "16:06:02 UTC" (if atLocalTime is false)
  if (!timestamp) return "";

  const date = new Date(timestamp * 1000);
  const options = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: atLocalTime ? undefined : "UTC",
  };
  const timeString = date.toLocaleTimeString("en-US", options);
  return atLocalTime ? timeString : `${timeString} UTC`;
}

export function getPrettyDateTime(timestamp, atLocalTime = true) {
  // e.g 1753891562 -> "Jul 30, 2025, 19:06:02" or "Jul 30, 2025, 16:06:02 UTC" (if atLocalTime is false)
  if (!timestamp) return "";

  return `${getPrettyDate(timestamp, atLocalTime)}, ${getReadableTime(
    timestamp,
    atLocalTime
  )}`;
}

// Assuming timestamp_utc is in ISO 8601 format (e.g., "2023-04-01T12:00:00Z")
export function daysPastSince(timestamp_utc) {
  const timestampDate = new Date(timestamp_utc);
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = currentDate - timestampDate;

  // Convert milliseconds to days (1 day = 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
  const differenceInDays = differenceInMilliseconds / (24 * 60 * 60 * 1000);

  return Math.floor(differenceInDays);
}

export function getCurrentTimestamp() {
  return Math.floor(Date.now() / 1000);
}

export const getTimestampXDaysAgo = (days) => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - days);
  return Math.floor(currentDate.getTime() / 1000);
};

export const getTimestampXDaysInFuture = (days) => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + days);
  return Math.floor(currentDate.getTime() / 1000);
};

const calculateTimeDifference = (timestamp1, timestamp2) => {
  // timestamp2 should always be later than timestamp1

  const date1 = new Date(timestamp1 * 1000);
  const date2 = new Date(timestamp2 * 1000);

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = date2 - date1;

  // Convert milliseconds to different units
  const differenceInSeconds = differenceInMilliseconds / 1000;
  const differenceInMinutes = differenceInSeconds / 60;
  const differenceInHours = differenceInMinutes / 60;
  const differenceInDays = differenceInHours / 24;
  const differenceInWeeks = differenceInDays / 7;
  const differenceInMonths = differenceInWeeks / (365 / 12 / 7); // average weeks in a month
  const differenceInYears = differenceInMonths / 12;

  return {
    differenceInSeconds,
    differenceInMinutes,
    differenceInHours,
    differenceInDays,
    differenceInWeeks,
    differenceInMonths,
    differenceInYears,
  };
};

export function timePassedSince(timestamp) {
  const {
    differenceInMinutes,
    differenceInHours,
    differenceInDays,
    differenceInWeeks,
    differenceInMonths,
    differenceInYears,
  } = calculateTimeDifference(timestamp, getCurrentTimestamp());

  if (differenceInMinutes < 1) return "Just now";
  else if (differenceInHours < 1) return `${Math.floor(differenceInMinutes)}m`;
  else if (differenceInHours < 24) return `${Math.floor(differenceInHours)}h`;
  else if (differenceInDays < 7) return `${Math.floor(differenceInDays)}d`;
  else if (differenceInWeeks < 365 / 12 / 7)
    return `${Math.floor(differenceInWeeks)}w`;
  else if (differenceInMonths < 12)
    return `${Math.floor(differenceInMonths)}mo`;
  else return `${Math.floor(differenceInYears)}y`;
}

export function updatedXTimeAgo(timestamp) {
  const {
    differenceInMinutes,
    differenceInHours,
    differenceInDays,
    differenceInWeeks,
    differenceInMonths,
    differenceInYears,
  } = calculateTimeDifference(timestamp, getCurrentTimestamp());

  if (differenceInMinutes < 1) return "Updated just now";
  else if (differenceInHours < 1)
    return formatUpdatedAgo(differenceInMinutes, "minute");
  else if (differenceInHours < 24)
    return formatUpdatedAgo(differenceInHours, "hour");
  else if (differenceInDays < 7)
    return formatUpdatedAgo(differenceInDays, "day");
  else if (differenceInWeeks < 365 / 12 / 7)
    return formatUpdatedAgo(differenceInWeeks, "week");
  else if (differenceInMonths < 12)
    return formatUpdatedAgo(differenceInMonths, "month");
  else return formatUpdatedAgo(differenceInYears, "year");
}

function formatUpdatedAgo(quantity, unit) {
  const value = Math.floor(quantity);
  return `Updated ${value} ${pluralize(unit, value)} ago`;
}

// TODO - this is too specific (for inbox notifications only) and should be moved from here
export const categorizeNotifications = (notifications) => {
  const categorized = {
    today: [],
    week: [],
    older: [],
  };

  notifications?.forEach((notification) => {
    const { differenceInHours, differenceInDays } = calculateTimeDifference(
      notification.date_object.timestamp_utc,
      getCurrentTimestamp()
    );

    if (differenceInHours < 24) {
      categorized.today.push(notification);
    } else if (differenceInDays < 7) {
      categorized.week.push(notification);
    } else {
      categorized.older.push(notification);
    }
  });

  return categorized;
};

export const getLocalIanaTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getUTCOffsetOfTimezone = (ianaTimezone) => {
  // example: "Asia/Jerusalem" -> 2 (or 3 in summer)

  const now = new Date();
  const utcDate = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
  const timezoneDate = new Date(
    now.toLocaleString("en-US", { timeZone: ianaTimezone })
  );
  const offsetMilliseconds = timezoneDate.getTime() - utcDate.getTime();
  return offsetMilliseconds / (1000 * 60 * 60);
};

export const useLocalTime = () => {
  const { user } = useContext(BootstrapContext);
  return user?.use_localtime ?? true;
};

/**
 * Formats a timestamp for display, hiding minutes when they are :00
 * e.g., "Nov 18, 2025, 11 AM" instead of "Nov 18, 2025, 11:00 AM"
 * but shows "Nov 18, 2025, 11:30 AM" when minutes are non-zero
 */
export const formatTimestampWithOptionalMinutes = (iso8601String) => {
  if (!iso8601String) return "N/A";

  const date = new Date(iso8601String);
  const hour = date.toLocaleString("en-US", {
    hour: "numeric",
    hour12: true,
  });
  const minutes = date.getMinutes();
  const dateStr = date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const timeStr =
    minutes === 0
      ? hour
      : `${hour.split(" ")[0]}:${minutes.toString().padStart(2, "0")} ${
          hour.split(" ")[1]
        }`;

  return `${dateStr}, ${timeStr}`;
};
